import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { startCase } from "lodash";

import MyAccountLayout from "./MyAccountLayout";
import CustomDatePicker from "../../components/CustomDatePicker";
import MyAccountFilter from "./MyAccountFilter";
import { apiGet } from "../../Utils/apiFetch";
import { apiPath } from "../../Utils/apiPath";
import { formatDate, toFixedWithoutRounding } from "../../Utils/Constant";
import AuthContext from "../../context/AuthContext";
import { useContextSelector } from "use-context-selector";
import Pagination from "../../components/Pagination";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

const TABS = [
  { key: "Exchange", label: "Exchange", betType: "betfair" },
  // { key: "FancyBet", label: "FancyBet", betType: "fancy" },
  // { key: "Sportsbook", label: "Sportsbook", betType: "sportBook" },
  // { key: "BookMaker", label: "BookMaker", betType: "bookmaker" },
  // { key: "Casino", label: "Casino", betType: "casino" },
  // { key: "parlay", label: "Parlay", betType: "parlay" },
];

const commissionFor = (marketSubTotal, commissionPct) =>
  marketSubTotal > 0 ? (marketSubTotal * commissionPct) / 100 : 0;

/* ------------------------------------------------------------------------ */
/*  P/L calculation helpers                                                 */
/* ------------------------------------------------------------------------ */
const num = (x) => Number(x || 0);
const isVoided = (v) => v.status === "voided";
const isSettled = (v) => !!v.isDeclared && !isVoided(v);

// true = win, false = loss
const isBetWin = (v) => {
  if (v.exchangeType === "session") {
    return v.type === "No"
      ? v.decisionRun < v.betRun
      : v.decisionRun >= v.betRun;
  }
  const winKey =
    v.exchangeType === "sportbook" ? "fancySelectionId" : "selectionId";
  const selWon = v.teamSelectionWin === v[winKey];
  return v.betType === "lay" ? !selWon : selWon;
};

const betPL = (v) => (isBetWin(v) ? num(v.profitAmount) : -num(v.loseAmount));

const MARKET_LABELS = {
  betfair: "Match Odds",
  bookmaker: "Bookmaker",
  session: "Fancy",
  sportbook: "Sportsbook",
};

const calcBets = (bets, exchangeType) => {
  const totalStake = bets.reduce((a, v) => a + num(v.amount), 0);
  const settled = bets.filter(isSettled);

  if (exchangeType === "session") {
    const yesSubTotal = settled
      .filter((v) => v.type === "Yes")
      .reduce((a, v) => a + betPL(v), 0);
    const noSubTotal = settled
      .filter((v) => v.type === "No")
      .reduce((a, v) => a + betPL(v), 0);
    const marketSubTotal = yesSubTotal + noSubTotal;
    return {
      type: "fancy",
      yesSubTotal,
      noSubTotal,
      marketSubTotal,
      netAmount: marketSubTotal,
      totalStake,
    };
  }

  const backSubTotal = settled
    .filter((v) => v.betType === "back")
    .reduce((a, v) => a + betPL(v), 0);
  const laySubTotal = settled
    .filter((v) => v.betType === "lay")
    .reduce((a, v) => a + betPL(v), 0);
  const marketSubTotal = backSubTotal + laySubTotal;
  return {
    type: "default",
    backSubTotal,
    laySubTotal,
    marketSubTotal,
    netAmount: marketSubTotal,
    totalStake,
  };
};

// one row can hold mixed markets -> group by bet.exchangeType, calc each separately
const marketPL = (item) => {
  const groups = (item?.bets_list || []).reduce((acc, b) => {
    const key = String(b.exchangeType || "unknown").toLowerCase();
    (acc[key] ||= []).push(b);
    return acc;
  }, {});

  const markets = Object.entries(groups).map(([exchangeType, list]) => ({
    exchangeType,
    label: MARKET_LABELS[exchangeType] || startCase(exchangeType),
    ...calcBets(list, exchangeType),
  }));

  return {
    markets,
    netAmount: markets.reduce((a, m) => a + m.netAmount, 0),
    totalStake: markets.reduce((a, m) => a + m.totalStake, 0),
  };
};

// API returns the same bets under several match docs ->
// keep each bet only under its own match, dedupe by _id
const normalizeRows = (rows) => {
  const seen = new Set();
  return (rows || [])
    .map((r) => ({
      ...r,
      bets_list: (r.bets_list || []).filter((b) => {
        if (b.matchId && b.matchId !== r._id) return false;
        if (seen.has(b._id)) return false;
        seen.add(b._id);
        return true;
      }),
    }))
    .filter((r) => r.bets_list.length > 0);
};

// same bet can appear under several rows -> dedupe by _id for the grand total
const uniqueBetsTotal = (rows) => {
  const unique = [
    ...new Map(
      (rows || []).flatMap((r) => r.bets_list || []).map((b) => [b._id, b]),
    ).values(),
  ];
  return marketPL({ bets_list: unique }).netAmount;
};

const rowsTotal = (rows, betType) => {
  if (betType === "casino") {
    return (rows || []).reduce(
      (sum, item) =>
        sum + Number(item?.playerPL ?? item?.transactions?.realCutAmount ?? 0),
      0,
    );
  }
  if (betType === "parlay") {
    return (rows || []).reduce((sum, item) => {
      if (!item?.isDeclared) return sum;
      return (
        sum +
        (item.result === "loss"
          ? -Number(item?.loseAmount || 0)
          : Number(item?.profitAmount || 0))
      );
    }, 0);
  }
  return uniqueBetsTotal(rows);
};

/* ------------------------------------------------------------------------ */

const BettingProfitLoss = () => {
  const params = useParams();
  const DEFAULT_FILTER = {
    page: 1,
    limit: 50,
    betType: "all",
    userType: params.type,
    id: params.id,
    fromPeriod: moment().subtract(1, "day").format("YYYY-MM-DD HH:mm:ss"),
    toPeriod: moment().format("YYYY-MM-DD HH:mm:ss"),
  };

  const tableRef = useRef();
  const user = useContextSelector(AuthContext, (ctx) => ctx.user);
  const [rowBetsData, setRowBetsData] = useState({}); // { [rowId]: apiResponseData }

  const [activeTab, setActiveTab] = useState("Exchange");
  const [data, setData] = useState([]);
  const [openRows, setOpenRows] = useState({});
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [commissionPct, setCommissionPct] = useState(0);
  const [totalDataPL, settotalDataPL] = useState([]);
  const getData = async (activeFilter) => {
    const { status, data: response_users } = await apiGet(
      apiPath.betProfitLoss,
      activeFilter,
    );
    if (status === 200 && response_users.success) {
      setData({
        ...response_users.pagination,
        data: response_users?.results.data,
      });
      settotalDataPL(response_users?.results?.totalRealCutAmount);
    }
  };
  const getCommission = async () => {
    if (!params?.id) return;
    const { status, data: response_users } = await apiGet(apiPath.userProfile, {
      user_id: params.id,
    });
    if (status === 200 && response_users.success) {
      setCommissionPct(response_users.results?.commission || 0);
    }
  };

  useEffect(() => {
    getData(filter);
  }, [filter.page, filter.limit, filter.betType]);

  useEffect(() => {
    getCommission();
  }, [params?.id]);

  const handleTypeChange = (tabKey) => {
    const tab = TABS.find((t) => t.key === tabKey) || TABS[0];
    setActiveTab(tabKey);
    setOpenRows({});
    setRowBetsData({});
    setFilter((prev) => ({
      ...prev,
      type: tabKey,
      betType: tab.betType,
      page: 1,
    }));
  };

  const handleLimitChange = (e) => {
    setFilter((prev) => ({ ...prev, limit: Number(e.target.value), page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilter((prev) => ({ ...prev, page: newPage }));
  };

  const applyPreset = (preset) => {
    const fromPeriod =
      preset === "today"
        ? moment().startOf("day")
        : moment().subtract(1, "day").startOf("day");
    const toPeriod =
      preset === "today"
        ? moment().endOf("day")
        : moment().subtract(1, "day").endOf("day");
    const nextFilter = {
      ...filter,
      page: 1,
      fromPeriod: fromPeriod.format("YYYY-MM-DD HH:mm:ss"),
      toPeriod: toPeriod.format("YYYY-MM-DD HH:mm:ss"),
    };
    setFilter(nextFilter);
    if (filter.page === nextFilter.page) getData(nextFilter);
  };

  const getRowBets = async (rowId, item) => {
    const object = {
      betFaireType: item?.betFaireType,
      eventId: item?.eventId,
      marketId: item?.marketId,
      userId: params.id,
    };

    const { status, data: response } = await apiGet(
      apiPath.userBetsnew,
      object,
    );
    if (status === 200 && response.success) {
      setRowBetsData((prev) => ({ ...prev, [rowId]: response.results }));
    }
  };
  const toggleRow = (id, item) => {
    const willOpen = !openRows[id];
    setOpenRows((prev) => ({ ...prev, [id]: willOpen }));
    if (willOpen && item && !rowBetsData[id]) {
      getRowBets(id, item);
    }
  };
  const redirectCasino = async (userId, platform, platformTxId, type) => {
    const { status, data: response_users } = await apiGet(
      apiPath.casinoWalletHistory +
        "?userId=" +
        userId +
        "&platform=" +
        platform +
        "&platformTxId=" +
        platformTxId,
    );
    if (status === 200 && response_users.status) {
      if (response_users.data?.status === "0000") {
        const url =
          type === 1 ? response_users.data?.txnUrl : response_users.data?.url;
        window.open(url, "_blank", "height=900,width=1200");
      } else {
        toast.error(response_users.data.desc);
      }
    } else {
      toast.error(response_users?.data?.desc);
    }
  };

  const currentBetType = filter.betType;
  const rows = data?.data || [];
  const totalPL = rowsTotal(rows, currentBetType);
  return (
    <MyAccountLayout>
      <div id="report" className="col-center report">
        <h2 _ngcontent-serverapp-c94>
          Betting Profit &amp; Loss : Main wallet
        </h2>
        <ul className="report-tab-wrap" style={{ display: "block" }}>
          {TABS.map((tab) => (
            <li
              key={tab.key}
              id={`reportType_${tab.betType}`}
              className={`report-tab ${activeTab === tab.key ? "select" : ""}`}
              style={{ cursor: "pointer" }}
              onClick={() => handleTypeChange(tab.key)}
            >
              {tab.label}
            </li>
          ))}
        </ul>

        {/* Filters */}
        <div className="function-wrap">
          <ul className="input-list">
            <li>
              <label>Period</label>
            </li>
            <CustomDatePicker filter={filter} setFilter={setFilter} />
          </ul>

          <ul className="input-list flowroot">
            <li>
              <a
                id="today"
                className="btn"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  applyPreset("today");
                }}
              >
                Just for today
              </a>
            </li>
            <li>
              <a
                id="yesterday"
                className="btn"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  applyPreset("yesterday");
                }}
              >
                From yesterday
              </a>
            </li>
            <li>
              <a
                id="getPL"
                className="btn-send"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  getData(filter);
                }}
              >
                Get P &amp; L
              </a>
            </li>
          </ul>
        </div>

        {/* Totals + limit selector */}
        <div id="userTreeDiv" className="total_all">
          <div
            style={{
              padding: "4px 12px",
              fontWeight: "bold",
              fontSize: "16px",
            }}
          >
            Total P/L:&nbsp;
            <span className={totalDataPL < 0 ? "red" : "green"}>
              {toFixedWithoutRounding(totalDataPL)}
            </span>
          </div>
          <span className="spacer" />
          <span>
            <label>Last:</label>
            <select
              name="limit"
              id="limit"
              value={filter.limit}
              onChange={handleLimitChange}
            >
              <option value={10}>10 Txn</option>{" "}
              <option value={25}>25 Txn</option>{" "}
              <option value={50}>50 Txn</option>
              <option value={100}>100 Txn</option>
            </select>
          </span>
          <MyAccountFilter
            type={"new"}
            tableRef={tableRef}
            fileName={"betting-profit-loss"}
          />
        </div>

        {/* Table */}
        <div id="reportTable">
          <div
            className="table-responsive data-table"
            style={{ overflow: "auto", whiteSpace: "nowrap" }}
          >
            {currentBetType === "casino" ? (
              <CasinoTable
                rows={rows}
                tableRef={tableRef}
                user={user}
                openRows={openRows}
                toggleRow={toggleRow}
                redirectCasino={redirectCasino}
                rowBetsData={rowBetsData}
              />
            ) : currentBetType === "parlay" ? (
              <ParlayTable
                rows={rows}
                tableRef={tableRef}
                openRows={openRows}
                toggleRow={toggleRow}
                rowBetsData={rowBetsData}
              />
            ) : (
              <DefaultTable
                rows={rows}
                tableRef={tableRef}
                user={user}
                betType={currentBetType}
                openRows={openRows}
                toggleRow={toggleRow}
                commissionPct={commissionPct}
                rowBetsData={rowBetsData}
              />
            )}
          </div>
          <div className="bottom-pagination">
            {rows.length > 0 && data?.totalRecords > 0 && (
              <Pagination pagination={data} onPageChange={handlePageChange} />
            )}
          </div>
        </div>
      </div>
    </MyAccountLayout>
  );
};

/* ------------------------------------------------------------------------ */
/*  Default table: Exchange / FancyBet / Sportsbook / BookMaker             */
/* ------------------------------------------------------------------------ */
function DefaultTable({
  rows,
  tableRef,
  user,
  betType,
  openRows,
  toggleRow,
  commissionPct,
  rowBetsData,
}) {
  return (
    <table
      ref={tableRef}
      id="table_DL"
      className="table-s"
      style={{ display: "table" }}
    >
      <tbody>
        <tr>
          <th className="align-L sort_asc">Market</th>
          <th width="15%" className="sort_asc">
            Start Time
          </th>
          <th width="15%" className="sort_asc">
            Settled Date
          </th>
          <th width="15%" className="sort_asc">
            Profit/Loss
          </th>
        </tr>

        {rows && rows.length > 0 ? (
          rows.map((item, index) => {
            const rowId = item?._id || index;
            const isOpen = !!openRows[rowId];
            const pl = marketPL({ bets_list: rowBetsData[item?._id] });
            const netAmount = pl.netAmount;
            const eventLabel =
              item?.eventType === "4"
                ? "Cricket"
                : item?.eventType === "1"
                  ? "Soccer"
                  : item?.eventType === "2"
                    ? "Tennis"
                    : "Sports";
            // console.log(rowBetsData[item?._id], "rowBetsData[item?._id]");
            return (
              <React.Fragment key={rowId}>
                <tr
                  style={{ display: "table-row" }}
                  className={item?.status === "voided" ? "voided-tr" : ""}
                >
                  <td className="align-L">
                    {eventLabel}
                    <img
                      src="/assets/images/transparent.gif"
                      className="fromto"
                      alt=""
                    />
                    <strong>{item?.gameName}</strong>
                    <img
                      src="/assets/images/transparent.gif"
                      className="fromto"
                      alt=""
                    />
                    {item?.runnerName}
                  </td>
                  <td>
                    <span className="small-date">
                      {formatDate(item?.createdAt, user?.timeZone)}
                    </span>
                  </td>
                  <td>
                    <span className="small-date">
                      {formatDate(item?.updatedAt, user?.timeZone)}
                    </span>
                  </td>
                  <td>
                    <a
                      style={{ paddingRight: "20px", float: "none" }}
                      onClick={() => toggleRow(rowId, item)}
                      className={!isOpen ? "expand-close" : "expand-open"}
                    >
                      {item?.status == "voided" ? (
                        <span className="red">(0.00)</span>
                      ) : item?.realCutAmount >= 0 ? (
                        <span className="green">
                          {toFixedWithoutRounding(item?.realCutAmount)}
                        </span>
                      ) : (
                        <span className="red">
                          (
                          {toFixedWithoutRounding(
                            Math.abs(item?.realCutAmount),
                          )}
                          )
                        </span>
                      )}
                    </a>
                  </td>
                </tr>
                {rowBetsData[item?._id]?.length > 0 && (
                  <tr
                    className="expand"
                    id={`sub_${rowId}`}
                    style={{ display: isOpen ? "table-row" : "none" }}
                  >
                    <td colSpan={5}>
                      <img
                        src="/assets/images/transparent.gif"
                        className="expand-arrow"
                        alt=""
                      />
                      <table id={`txTable_${rowId}`}>
                        <tbody>
                          <tr>
                            <th>PL ID</th>
                            <th>Bet ID</th>
                            <th>Selection</th>
                            <th>Odds</th>
                            <th>Stake</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Placed</th>
                            <th>Profit/Loss</th>
                          </tr>
                          {rowBetsData[item?._id].map((betData, i) => {
                            const isWin = isBetWin(betData);
                            return (
                              <tr
                                key={betData?._id || i}
                                className={
                                  betData?.status === "voided"
                                    ? "voided-tr even"
                                    : "even"
                                }
                                style={{ display: "table-row" }}
                              >
                                <td>{betData?.clientName}</td>
                                <td>
                                  {betData?.sessionBetId || betData?.matchBetId}
                                </td>
                                <td>
                                  {betData?.exchangeType === "session"
                                    ? `${betData?.fancyName}`
                                    : betData?.runnerName || betData?.teamName}
                                </td>
                                <td>
                                  {betData?.exchangeType === "session"
                                    ? `${betData?.betRun}/${betData?.bhav}`
                                    : betData?.bhav}
                                </td>
                                <td>{betData?.amount}</td>
                                <td>
                                  <span
                                    className={
                                      betData?.exchangeType === "session"
                                        ? betData?.type === "No"
                                          ? "lay"
                                          : "back"
                                        : betData?.betType === "lay"
                                          ? "lay"
                                          : "back"
                                    }
                                  >
                                    {betData?.exchangeType === "session"
                                      ? betData?.type
                                      : startCase(betData?.betType)}
                                  </span>
                                </td>
                                <td>{startCase(betData?.status)}</td>
                                <td>
                                  <span className="small-date">
                                    {formatDate(
                                      betData?.createdAt,
                                      user?.timeZone,
                                    )}
                                  </span>
                                </td>
                                <td>
                                  {betData?.status === "voided" ? (
                                    <span>0.00</span>
                                  ) : !betData?.isDeclared ? (
                                    <span>-</span>
                                  ) : isWin ? (
                                    <span className="green">
                                      {toFixedWithoutRounding(
                                        betData?.profitAmount,
                                      )}
                                    </span>
                                  ) : (
                                    <span className="red">
                                      (
                                      {toFixedWithoutRounding(
                                        Math.abs(betData?.loseAmount),
                                      )}
                                      )
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}

                          <tr className="sum-pl">
                            <td colSpan={9}>
                              {pl.markets.map((m) => {
                                const commission = commissionFor(
                                  m.marketSubTotal,
                                  commissionPct,
                                );
                                const cls = (n) => (n < 0 ? "red" : "green");
                                return (
                                  <dl key={m.exchangeType}>
                                    {/* <dt>
                                      <strong>{m.label}</strong>
                                    </dt>
                                    <dd /> */}
                                    <dt>Total Stakes</dt>
                                    <dd>
                                      {toFixedWithoutRounding(m.totalStake)}
                                    </dd>

                                    {m?.exchangeType === "session" ? (
                                      <>
                                        <dt>Yes subtotal</dt>
                                        <dd className={cls(m.yesSubTotal)}>
                                          {toFixedWithoutRounding(
                                            Math.abs(m.yesSubTotal),
                                          )}
                                        </dd>
                                        <dt>No subtotal</dt>
                                        <dd className={cls(m.noSubTotal)}>
                                          {toFixedWithoutRounding(
                                            Math.abs(m.noSubTotal),
                                          )}
                                        </dd>
                                      </>
                                    ) : (
                                      <>
                                        <dt>Back subtotal</dt>
                                        <dd className={cls(m.backSubTotal)}>
                                          {toFixedWithoutRounding(
                                            Math.abs(m.backSubTotal),
                                          )}
                                        </dd>
                                        <dt>Lay subtotal</dt>
                                        <dd className={cls(m.laySubTotal)}>
                                          {toFixedWithoutRounding(
                                            Math.abs(m.laySubTotal),
                                          )}
                                        </dd>
                                      </>
                                    )}

                                    <dt>Market subtotal</dt>
                                    <dd className={cls(m.marketSubTotal)}>
                                      {toFixedWithoutRounding(
                                        Math.abs(m.marketSubTotal),
                                      )}
                                    </dd>

                                    {m?.exchangeType != "session" && (
                                      <>
                                        <dt>Commission</dt>
                                        <dd>
                                          {toFixedWithoutRounding(commission)}
                                        </dd>
                                      </>
                                    )}
                                  </dl>
                                );
                              })}

                              <dl>
                                <dt className="net_total">Net Market Total</dt>
                                <dd
                                  className={`net_total ${netAmount < 0 ? "red" : "green"}`}
                                >
                                  {netAmount >= 0
                                    ? toFixedWithoutRounding(netAmount)
                                    : `(${toFixedWithoutRounding(Math.abs(netAmount))})`}
                                </dd>
                              </dl>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })
        ) : (
          <tr>
            <td colSpan={3}>You have no bets in this time period.</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/* ------------------------------------------------------------------------ */
/*  Casino table                                                            */
/* ------------------------------------------------------------------------ */
function CasinoTable({
  rows,
  tableRef,
  user,
  openRows,
  toggleRow,
  redirectCasino,
}) {
  return (
    <table
      ref={tableRef}
      id="table_DL"
      className="table-s"
      style={{ display: "table" }}
    >
      <tbody>
        <tr>
          <th className="align-L sort_asc">Market</th>
          <th width="10%" className="sort_asc">
            Profit/Loss
          </th>
        </tr>

        {rows && rows.length > 0 ? (
          rows.map((item, index) => {
            const rowId = item?.platformTxId || index;
            const isOpen = !!openRows[rowId];
            const pl = Number(
              item?.playerPL ?? item?.transactions?.realCutAmount ?? 0,
            );

            return (
              <React.Fragment key={rowId}>
                <tr style={{ display: "table-row" }}>
                  <td className="align-L">
                    Casino
                    <img
                      src="/assets/images/transparent.gif"
                      className="fromto"
                      alt=""
                    />
                    <strong> {item?.casinoName}</strong>
                    <img
                      src="/assets/images/transparent.gif"
                      className="fromto"
                      alt=""
                    />
                    <span className="small-date">
                      {formatDate(item?.timeInserted, user?.timeZone)}
                    </span>
                  </td>
                  <td>
                    <a
                      onClick={() => toggleRow(rowId)}
                      className={isOpen ? "expand-close" : "expand-open"}
                    >
                      {Math.sign(pl) === -1 ? (
                        <span className="red">
                          ({toFixedWithoutRounding(pl)})
                        </span>
                      ) : (
                        <span className="green">
                          ({toFixedWithoutRounding(Math.abs(pl))})
                        </span>
                      )}
                    </a>
                  </td>
                </tr>

                <tr
                  className="expand"
                  id={`sub_${rowId}`}
                  style={{ display: isOpen ? "table-row" : "none" }}
                >
                  <td colSpan={2}>
                    <table>
                      <tbody>
                        <tr>
                          <th>Platform</th>
                          <th>Bet Amount</th>
                          <th>Win/Loss</th>
                          <th>Profit/Loss</th>
                          <th>Action</th>
                        </tr>
                        <tr className="even" style={{ display: "table-row" }}>
                          <td>
                            <a
                              onClick={() =>
                                redirectCasino(
                                  item?.clientName,
                                  item?.platform,
                                  item?.platformTxId,
                                  1,
                                )
                              }
                            >
                              {item?.platform}
                            </a>
                          </td>
                          <td>{item?.betAmount}</td>
                          <td className={pl < 0 ? "red" : "green"}>
                            {toFixedWithoutRounding(pl)}
                          </td>
                          <td className={pl < 0 ? "red" : "green"}>
                            {toFixedWithoutRounding(pl)}
                          </td>
                          <td>
                            <a
                              onClick={() =>
                                redirectCasino(
                                  item?.clientName,
                                  item?.platform,
                                  item?.platformTxId,
                                  2,
                                )
                              }
                            >
                              Get Result
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </React.Fragment>
            );
          })
        ) : (
          <tr>
            <td colSpan={2}>You have no bets in this time period.</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/* ------------------------------------------------------------------------ */
/*  Parlay table                                                            */
/* ------------------------------------------------------------------------ */
function ParlayTable({ rows, tableRef, openRows, toggleRow }) {
  return (
    <table
      ref={tableRef}
      id="table_DL"
      className="table-s"
      style={{ display: "table" }}
    >
      <tbody>
        <tr>
          <th className="align-L sort_asc">Market</th>
          <th className="sort_asc">Settled Date</th>
          <th className="sort_asc">Bet ID</th>
          <th className="sort_asc">Stake</th>
          <th className="sort_asc">Profit/Loss</th>
        </tr>

        {rows && rows.length > 0 ? (
          rows.map((item, index) => {
            const rowId = item?._id || index;
            const isOpen = !!openRows[rowId];
            const legLabel =
              item?.bets?.length === 1
                ? "SINGLE"
                : item?.bets?.length === 2
                  ? "DOUBLE"
                  : item?.bets?.length === 3
                    ? "TREBLE"
                    : item?.bets?.length > 3
                      ? "ACCUMULATOR"
                      : "";

            return (
              <React.Fragment key={rowId}>
                <tr style={{ display: "table-row" }}>
                  <td className="align-L">
                    {item?.isDeclared ? (
                      <strong
                        className={item?.result === "loss" ? "red" : "green"}
                      >
                        {startCase(item?.result)}
                      </strong>
                    ) : (
                      "..."
                    )}
                    <img
                      src="/assets/images/transparent.gif"
                      className="fromto"
                      alt=""
                    />
                    <strong>{legLabel}</strong>
                  </td>
                  <td>{formatDate(item?.timeInserted)}</td>
                  <td>{item?.matchBetId}</td>
                  <td>{item?.amount}</td>
                  <td>
                    {item?.isDeclared ? (
                      item?.result === "loss" ? (
                        <strong className="red">
                          {toFixedWithoutRounding(item?.loseAmount)}
                        </strong>
                      ) : (
                        <strong className="green">
                          {toFixedWithoutRounding(item?.profitAmount)}
                        </strong>
                      )
                    ) : (
                      "..."
                    )}
                    {item?.bets?.length > 0 && (
                      <a
                        onClick={() => toggleRow(rowId)}
                        className={isOpen ? "expand-close" : "expand-open"}
                        style={{ marginLeft: 6, cursor: "pointer" }}
                      />
                    )}
                  </td>
                </tr>

                {item?.bets?.length > 0 && (
                  <tr
                    className="expand"
                    id={`sub_${rowId}`}
                    style={{ display: isOpen ? "table-row" : "none" }}
                  >
                    <td colSpan={5}>
                      <table>
                        <tbody>
                          <tr>
                            <th className="align-L">Fancy Name</th>
                            <th>Match</th>
                            <th>Selection</th>
                            <th>Odds</th>
                            <th>Result</th>
                          </tr>
                          {item.bets.map((betData, i) => (
                            <tr
                              key={i}
                              className="even"
                              style={{ display: "table-row" }}
                            >
                              <td>{betData?.fancyName}</td>
                              <td>{betData?.matchName}</td>
                              <td>{betData?.runnerName}</td>
                              <td>{betData?.bhav}</td>
                              <td>
                                {betData?.isDeclared ? (
                                  <strong
                                    className={
                                      betData?.result === "loss"
                                        ? "red"
                                        : "green"
                                    }
                                  >
                                    {startCase(betData?.result)}
                                  </strong>
                                ) : (
                                  "..."
                                )}
                              </td>
                            </tr>
                          ))}
                          <tr className="sum-pl">
                            <td colSpan={5}>
                              <dl>
                                <dt className="net_total">Net Market Total</dt>
                                <dd
                                  className={`net_total ${item?.result === "loss" ? "red" : "green"}`}
                                >
                                  {item?.isDeclared
                                    ? item?.result === "loss"
                                      ? toFixedWithoutRounding(item?.loseAmount)
                                      : toFixedWithoutRounding(
                                          item?.profitAmount,
                                        )
                                    : "..."}
                                </dd>
                              </dl>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })
        ) : (
          <tr>
            <td colSpan={5}>You have no bets in this time period.</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

export default BettingProfitLoss;
